// NewStudentListing.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc
} from 'firebase/firestore';

import { db } from '../../../firebase';
import { useAuth } from '../../../../contexts/AuthContext';

import NewSearchComponent from './NewSearchComponent';
import CampaignModal from './CampaignModal';
import TemplateModal from './TemplateModal';

/** If you need random lastLogin, keep or remove this helper. */
function generateWeightedRandomDateString() {
  const totalWeight = 6; // 1 (Nov) + 2 (Dec) + 3 (Jan)
  const rand = Math.random() * totalWeight;
  let selectedMonth;

  if (rand < 1) {
    selectedMonth = 'november';
  } else if (rand < 3) {
    selectedMonth = 'december';
  } else {
    selectedMonth = 'january';
  }

  let year, monthIndex, maxDays;
  if (selectedMonth === 'november') {
    year = 2024;
    monthIndex = 10; // 0-based => 10 = November
    maxDays = 30;
  } else if (selectedMonth === 'december') {
    year = 2024;
    monthIndex = 11; // December
    maxDays = 31;
  } else {
    year = 2025;
    monthIndex = 0; // January
    maxDays = 15;
  }

  const day = Math.floor(Math.random() * maxDays) + 1;
  const randomDate = new Date(year, monthIndex, day);
  return randomDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/** Helper function for sorting students by a given column. */
function sortStudents(arr, sortKey, ascending) {
  if (!sortKey) return arr;

  const sorted = [...arr].sort((a, b) => {
    let aVal, bVal;

    switch (sortKey) {
      case 'name':
        aVal = a.name?.toLowerCase() || '';
        bVal = b.name?.toLowerCase() || '';
        break;

      case 'major':
        aVal = Array.isArray(a.major)
          ? a.major.join(',').toLowerCase()
          : (a.major?.toLowerCase() || '');
        bVal = Array.isArray(b.major)
          ? b.major.join(',').toLowerCase()
          : (b.major?.toLowerCase() || '');
        break;

      case 'gradYear':
        // Numeric comparison. If missing, treat as 0.
        aVal = a.surveyAnswers?.SchoolEnd?.year || 0;
        bVal = b.surveyAnswers?.SchoolEnd?.year || 0;
        break;

      case 'lastLogin':
        // Parse date strings to actual Date objects. If invalid, fallback to 0.
        {
          const aDate = new Date(a.lastLoginDate || '');
          const bDate = new Date(b.lastLoginDate || '');
          aVal = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
          bVal = isNaN(bDate.getTime()) ? 0 : bDate.getTime();
        }
        break;

      default:
        aVal = '';
        bVal = '';
    }

    if (aVal < bVal) return ascending ? -1 : 1;
    if (aVal > bVal) return ascending ? 1 : -1;
    return 0;
  });

  return sorted;
}

const NewStudentListing = ({
  students = [],
  advisors
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  /** Show all students initially, no “Search” needed to see them. */
  const [filteredStudents, setFilteredStudents] = useState(students);

  // Selection and pagination
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 7; // Only allow 7 entries per page

  // Sorting config
  const [sortConfig, setSortConfig] = useState({ key: null, asc: true });

  // Dropdown state & ref
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /** Fetch stored selectedStudents from Firestore on mount. */
  useEffect(() => {
    const fetchSelectedStudents = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setSelectedStudents(userDoc.data().selectedStudents || []);
          }
        } catch (error) {
          console.error('Error fetching selectedStudents:', error);
        }
      }
    };
    fetchSelectedStudents();
  }, [currentUser]);

  // If `students` prop changes, reset the filtered list
  useEffect(() => {
    setFilteredStudents(students);
  }, [students]);

  // Re-sort whenever sortConfig changes
  useEffect(() => {
    if (sortConfig.key) {
      setFilteredStudents((prev) => sortStudents(prev, sortConfig.key, sortConfig.asc));
    }
  }, [sortConfig]);

  // Visible slice for the current page
  const currentTableData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredStudents.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredStudents, currentPage]);

  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);
  const totalRecords = filteredStudents.length;

  // For "Showing X-Y of Z"
  const startIndex = totalRecords === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIndex =
    totalRecords === 0
      ? 0
      : Math.min(startIndex + rowsPerPage - 1, totalRecords);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  /** Click a column header => toggle sort for that field. */
  const handleSortColumn = (fieldKey) => {
    setSortConfig((prev) => {
      if (prev.key === fieldKey) {
        return { key: fieldKey, asc: !prev.asc };
      }
      return { key: fieldKey, asc: true };
    });
  };

  /** Called by NewSearchComponent => filters the full students list. */
  const handleSearch = useCallback(
    (searchFilters) => {
      let newFiltered = [...students];

      // 1) Basic name
      if (searchFilters.searchTerm) {
        newFiltered = newFiltered.filter((stu) =>
          stu.name?.toLowerCase().includes(searchFilters.searchTerm.toLowerCase())
        );
      }

      // 2) gradYear
      if (searchFilters.gradYear?.length > 0) {
        newFiltered = newFiltered.filter((stu) =>
          searchFilters.gradYear.includes(stu.surveyAnswers?.SchoolEnd?.year)
        );
      }

      // 3) program
      if (searchFilters.program?.length > 0) {
        newFiltered = newFiltered.filter((stu) => {
          if (Array.isArray(stu.major)) {
            return stu.major.some((prog) => searchFilters.program.includes(prog));
          }
          return searchFilters.program.includes(stu.major);
        });
      }

      // 4) advisors
      if (searchFilters.advisors?.length > 0) {
        newFiltered = newFiltered.filter((stu) =>
          stu.assignedAdvisorNames?.some((advName) =>
            searchFilters.advisors.includes(advName)
          )
        );
      }

      // 5) persistence
      if (searchFilters.persistence?.length > 0) {
        newFiltered = newFiltered.filter(
          (stu) =>
            stu.persistenceCategory &&
            searchFilters.persistence.includes(stu.persistenceCategory)
        );
      }

      // 6) customFilter
      if (
        searchFilters.customField &&
        searchFilters.customField.field &&
        searchFilters.customField.operator &&
        searchFilters.customField.value
      ) {
        const { field, operator, value } = searchFilters.customField;
        newFiltered = newFiltered.filter((stu) => {
          const val = stu[field];
          if (val == null) return false;

          if (operator === 'equals') {
            if (Array.isArray(val)) {
              return val.some(
                (v) => String(v).toLowerCase() === value.trim().toLowerCase()
              );
            }
            return String(val).trim().toLowerCase() === value.trim().toLowerCase();
          } else if (operator === 'greater than') {
            return parseFloat(val) > parseFloat(value);
          } else if (operator === 'less than') {
            return parseFloat(val) < parseFloat(value);
          }
          return false;
        });
      }

      // Re-apply sorting if a column is active
      const finalData = sortStudents(newFiltered, sortConfig.key, sortConfig.asc);
      setFilteredStudents(finalData);
      setCurrentPage(1);
    },
    [students, sortConfig.key, sortConfig.asc]
  );

  const allSelectedAll =
    filteredStudents.length > 0 &&
    filteredStudents.every((stu) => selectedStudents.includes(stu.id));

  const handleSelectAllAll = useCallback(async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const allStudentIds = filteredStudents.map((s) => s.id);
      let updated;
      if (allSelectedAll) {
        // Unselect all filtered students
        updated = selectedStudents.filter((id) => !allStudentIds.includes(id));
      } else {
        // Select all filtered students
        const newSet = new Set([...selectedStudents, ...allStudentIds]);
        updated = Array.from(newSet);
      }
      await updateDoc(userRef, { selectedStudents: updated });
      setSelectedStudents(updated);
    } catch (err) {
      console.error('Error selecting/unselecting students:', err);
      toast.error('Failed to update selection.');
    }
  }, [currentUser, filteredStudents, allSelectedAll, selectedStudents]);    

  /** Single student checkbox. */
  const handleCheckboxChange = useCallback(
    async (studentId, isChecked) => {
      if (!currentUser) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        if (isChecked) {
          await updateDoc(userRef, { selectedStudents: arrayUnion(studentId) });
          setSelectedStudents((prev) => [...prev, studentId]);
        } else {
          await updateDoc(userRef, { selectedStudents: arrayRemove(studentId) });
          setSelectedStudents((prev) => prev.filter((id) => id !== studentId));
        }
      } catch (error) {
        console.error('Error updating selectedStudents:', error);
        toast.error('Failed to update selection.');
      }
    },
    [currentUser]
  );

  /**
   * Navigates to /resources with selected student IDs (for resource recommending).
   */
  const handleResourcesClick = useCallback(() => {
    if (selectedStudents.length === 0) {
      toast.warning('Please select at least one student to recommend a resource.');
      return;
    }
    navigate('/resources', { state: { selectedStudentIds: selectedStudents } });
  }, [selectedStudents, navigate]);

  // Modals
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);

  const toggleTemplateModalVisibility = () => {
    if (selectedStudents.length === 0) {
      toast.warning('Please select student(s) to apply a template.');
    } else {
      setIsTemplateModalVisible(!isTemplateModalVisible);
    }
  };

  const handleCampaignClick = () => {
    if (selectedStudents.length === 0) {
      toast.warning('Please select at least one student.');
      return;
    }
    setIsCampaignModalOpen(true);
  };

  return (
    <div className="flex w-full">
      {/* LEFT SIDE: Filters */}
      <div className="w-1/3">
        <NewSearchComponent onSearch={handleSearch} advisors={advisors} />
      </div>

      {/* RIGHT SIDE: Table */}
      <div className="w-full ml-3">
        <div className="p-4 bg-gray-50 shadow-md border rounded-lg min-h-[70vh]">
          {/* Header (title + dropdown) */}
          <div className="flex items-center justify-center mb-4 relative">
            <h5 className="text-lg font-bold text-center">Students</h5>

            {/* Dropdown for "Templates", "Recommend Resource", "Send Campaign" */}
            <div
              className="absolute top-0 right-2 inline-block text-left"
              ref={dropdownRef}
            >
              <button
                type="button"
                onClick={() => {
                  if (selectedStudents.length > 0) {
                    setIsDropdownOpen(!isDropdownOpen);
                  }
                }}
                className={`text-white text-sm py-2 px-3 rounded inline-flex items-center
                  ${
                    selectedStudents.length === 0
                      ? 'bg-blue-300 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-700'
                  }`}
              >
                Student Actions
                <svg
                  className="w-2.5 h-2.5 ml-2"
                  aria-hidden="true"
                  fill="none"
                  viewBox="0 0 10 6"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 4 4 4-4"
                  />
                </svg>
              </button>

              {isDropdownOpen && (
                <div
                  className="z-10 mt-2 bg-white divide-y divide-gray-100
                  rounded-lg shadow w-60 absolute right-0"
                >
                  <ul className="p-2 space-y-1 text-sm text-gray-700">
                    <li>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          toggleTemplateModalVisibility();
                        }}
                        className="flex flex-col p-2 rounded hover:bg-gray-100 w-full text-left focus:outline-none"
                      >
                        <span className="font-medium text-gray-900">
                          Templates
                        </span>
                        <span className="text-xs font-normal text-gray-500">
                          Manage multi-term course planning templates.
                        </span>
                      </button>
                    </li>

                    <li>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          handleResourcesClick();
                        }}
                        className="flex flex-col p-2 rounded hover:bg-gray-100 w-full text-left focus:outline-none"
                      >
                        <span className="font-medium text-gray-900">
                          Recommend Resource
                        </span>
                        <span className="text-xs font-normal text-gray-500">
                          Suggest a campus tool or service.
                        </span>
                      </button>
                    </li>

                    <li>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          handleCampaignClick();
                        }}
                        className="flex flex-col p-2 rounded hover:bg-gray-100 w-full text-left focus:outline-none"
                      >
                        <span className="font-medium text-gray-900">
                          Send Campaign
                        </span>
                        <span className="text-xs font-normal text-gray-500">
                          Broadcast an email or message to selected students.
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* TABLE WITH NEW SELECT-ALL CHECKBOX */}
          <div className="relative overflow-x-auto">
            <table
              className="table-fixed w-full text-sm text-left text-gray-500"
              id="sorting-table"
            >
              <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                <tr>
                  {/* "Select All" header with icon */}
                  <th scope="col" className="w-20 py-2 text-center">
                    <div className="flex items-center justify-center">
                      <input
                        id="checkbox-all-search"
                        type="checkbox"
                        className="mt-2 w-4 h-4 text-indigo-600 border-gray-300 rounded cursor-pointer hover:bg-gray-300"
                        onChange={() => handleSelectAllAll()}
                        checked={allSelectedAll}
                      />
                      <label htmlFor="checkbox-all-search" className="sr-only">
                        checkbox
                      </label>
                    </div>
                  </th>

                  {/* Name */}
                  <th
                    scope="col"
                    className="w-64 px-4 py-3 cursor-pointer"
                    onClick={() => handleSortColumn('name')}
                  >
                    <span className="flex items-center">
                      Name
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m8 15 4 4 4-4m0-6-4-4-4 4"
                        />
                      </svg>
                    </span>
                  </th>

                  {/* Major/Program */}
                  <th
                    scope="col"
                    className="w-56 px-4 py-3 cursor-pointer"
                    onClick={() => handleSortColumn('major')}
                  >
                    <span className="flex items-center">
                      Major/Program
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m8 15 4 4 4-4m0-6-4-4-4 4"
                        />
                      </svg>
                    </span>
                  </th>

                  {/* Grad Year */}
                  <th
                    scope="col"
                    className="w-36 px-4 py-3 cursor-pointer whitespace-nowrap"
                    onClick={() => handleSortColumn('gradYear')}
                  >
                    <div className="flex items-center justify-between">
                      <span>Grad Year</span>
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m8 15 4 4 4-4m0-6-4-4-4 4"
                        />
                      </svg>
                    </div>
                  </th>

                  {/* Last Login */}
                  <th
                    scope="col"
                    className="w-36 px-4 py-3 cursor-pointer whitespace-nowrap"
                    onClick={() => handleSortColumn('lastLogin')}
                  >
                    <div className="flex items-center justify-between">
                      <span>Last Login</span>
                      <svg
                        className="w-4 h-4 ml-1"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m8 15 4 4 4-4m0-6-4-4-4 4"
                        />
                      </svg>
                    </div>
                  </th>

                  {/* Action */}
                  <th scope="col" className="text-center w-32 ml-2 py-3">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {currentTableData.map((student) => {
                  const isChecked = selectedStudents.includes(student.id);
                  const lastLogin =
                    student.lastLoginDate || generateWeightedRandomDateString();

                  return (
                    <tr
                      key={student.id}
                      className="bg-white border-b hover:!bg-gray-50 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/Profile/${student.id}`);
                      }}
                    >
                      {/* Checkbox */}
                      <td className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            handleCheckboxChange(student.id, e.target.checked)
                          }
                          className="mt-2 w-4 h-4 text-indigo-600 border-gray-300 rounded"
                        />
                      </td>

                      {/* Name */}
                      <td className="w-64 px-4 py-3 font-medium text-gray-900">
                        <div className="flex items-center">
                          {student.photoURL ? (
                            <img
                              src={student.photoURL}
                              alt="Profile"
                              className="w-8 h-8 rounded-full mr-2"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full mr-2 flex items-center justify-center bg-gray-400 text-white font-semibold">
                              {student.name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .substring(0, 2)}
                            </div>
                          )}
                          <div>
                            <h4 className="font-bold text-black">{student.name}</h4>
                            <p className="text-xs text-gray-500">{student.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Major/Program */}
                      <td className="w-56 px-4 py-3">
                        {Array.isArray(student.major)
                          ? student.major.join(', ')
                          : student.major}
                      </td>

                      {/* Grad Year */}
                      <td className="w-36 px-4 py-3 whitespace-nowrap">
                        {student.surveyAnswers?.SchoolEnd?.year || 'N/A'}
                      </td>

                      {/* Last Login */}
                      <td className="w-36 px-4 py-3 whitespace-nowrap">
                        {lastLogin}
                      </td>

                      {/* Action */}
                      <td className="w-32 px-4 py-3">
                        <div className="inline-flex border border-blue-700 rounded overflow-hidden text-xs w-auto">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/Profile/${student.id}`);
                            }}
                            className="flex-1 text-blue-700 hover:text-white hover:bg-blue-800 py-1 px-1 transition-all duration-300 focus:outline-none"
                          >
                            Profile
                          </button>
                          <div className="w-px bg-gray-300"></div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/Plan/${student.id}`);
                            }}
                            className="flex-1 text-blue-700 hover:text-white hover:bg-blue-800 px-2.5 py-1 transition-all duration-300 focus:outline-none text-center"
                          >
                            Plan
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* New Tailwind Snippet-Style Pagination */}
          <nav
            className="flex items-center flex-col flex-wrap md:flex-row justify-between pt-4"
            aria-label="Table navigation"
          >
            {/* "Showing X-Y of Z" */}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 mb-4 md:mb-0 block w-full md:inline md:w-auto">
              Showing{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {startIndex}-{endIndex}
              </span>{' '}
              of{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {totalRecords}
              </span>
            </span>

            {/* Numbered Pages + Next/Prev */}
            <ul className="inline-flex -space-x-px rtl:space-x-reverse text-sm h-8">
              {/* Previous */}
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePreviousPage();
                  }}
                  className={`flex items-center justify-center px-3 h-8 leading-tight border border-gray-300 rounded-s-lg
                    ${
                      currentPage === 1
                        ? 'text-gray-500 bg-white cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:!bg-gray-100 hover:!text-gray-700'
                    }
                    dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:!bg-gray-700 dark:hover:!text-white`}
                >
                  Previous
                </a>
              </li>

              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <li key={pageNum}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(pageNum);
                    }}
                    aria-current={pageNum === currentPage ? 'page' : undefined}
                    className={`flex items-center justify-center px-2.5 h-8 leading-tight border
                      ${
                        pageNum === currentPage
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-500 bg-white hover:!bg-gray-100 hover:!text-gray-700'
                      }
                      dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:!bg-gray-700 dark:hover:!text-white`}
                  >
                    {pageNum}
                  </a>
                </li>
              ))}

              {/* Next */}
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNextPage();
                  }}
                  className={`flex items-center justify-center px-3 h-8 leading-tight border border-gray-300 rounded-e-lg
                    ${
                      currentPage === totalPages || totalPages === 0
                        ? 'text-gray-500 bg-white cursor-not-allowed'
                        : 'text-gray-500 bg-white hover:!bg-gray-100 hover:!text-gray-700'
                    }
                    dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:!bg-gray-700 dark:hover:!text-white`}
                >
                  Next
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Campaign Modal */}
      {isCampaignModalOpen && (
        <CampaignModal
          onClose={() => setIsCampaignModalOpen(false)}
          selectedStudents={students.filter((stu) =>
            selectedStudents.includes(stu.id)
          )}
        />
      )}

      {/* Template Modal */}
      <TemplateModal
        isTemplateModalVisible={isTemplateModalVisible}
        setIsTemplateModalVisible={setIsTemplateModalVisible}
        selectedStudents={selectedStudents}
      />
    </div>
  );
};

export default NewStudentListing;
